import { useEffect, useRef, useState } from 'react';
import { Bell, Download, Search, FileText, Table, Loader2, ChevronDown } from 'lucide-react';
import { TerravisionMark } from './TerravisionMark';
import { TerritorySelector } from './TerritorySelector';
import { useTerritoryStore } from '@/stores/territoryStore';
import api from '@/services/api';

interface HeaderProps {
  onToggleSidebar?: () => void;
  /** Kept for backward-compat — not surfaced in J5 V2 design */
  onToggleLayers?: () => void;
}

type ExportKind = 'pdf' | 'csv-buildings' | 'csv-indicators';

const EXPORT_ITEMS: ReadonlyArray<{ kind: ExportKind; label: string; icon: typeof FileText }> = [
  { kind: 'pdf', label: 'Diagnostic territorial (PDF)', icon: FileText },
  { kind: 'csv-buildings', label: 'Bâtiments (CSV)', icon: Table },
  { kind: 'csv-indicators', label: 'Indicateurs (CSV)', icon: Table },
];

export function Header({ onToggleSidebar: _onToggleSidebar, onToggleLayers: _onToggleLayers }: HeaderProps) {
  const { activeTerritory } = useTerritoryStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState<ExportKind | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const download = async (kind: ExportKind) => {
    if (!activeTerritory) return;
    const insee = activeTerritory.code_insee;
    setBusy(kind);
    try {
      let path: string;
      let filename: string;
      let mime: string;
      if (kind === 'pdf') {
        path = `/exports/pdf/${insee}`;
        filename = `diagnostic_${activeTerritory.name}_${insee}.pdf`;
        mime = 'application/pdf';
      } else {
        const layer = kind === 'csv-buildings' ? 'buildings' : 'indicators';
        path = `/exports/csv/${insee}?layer=${layer}`;
        filename = `${layer}_${insee}.csv`;
        mime = 'text/csv';
      }
      const resp = await api.get(path, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: mime }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      setMenuOpen(false);
    } catch {
      window.alert('Export indisponible pour ce territoire.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <header
      className="flex h-[60px] flex-shrink-0 items-center justify-between bg-white px-5"
      style={{ borderBottom: '1px solid #e4e8f0' }}
    >
      {/* ── LEFT: mark + label + territory pill ── */}
      <div className="flex items-center gap-3">
        <TerravisionMark size={34} />

        <span className="hidden font-display text-[15px] font-bold tracking-tight text-ink sm:block">
          Terravision
        </span>

        <TerritorySelector />
      </div>

      {/* ── RIGHT: search · export · bell · avatar ── */}
      <div className="flex items-center gap-2">
        {/* Search */}
        <div className="relative hidden items-center md:flex">
          <Search size={13} className="pointer-events-none absolute left-2.5 text-muted" />
          <input
            type="search"
            placeholder="Rechercher…"
            className="h-8 w-44 rounded-[11px] border border-line bg-surface-subtle pl-7 pr-3 font-sans text-xs text-body placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-navy"
          />
        </div>

        {/* Exporter — menu de téléchargement (PDF / CSV) câblé sur l'API */}
        <div ref={ref} className="relative">
          <button
            type="button"
            onClick={() => activeTerritory && setMenuOpen((o) => !o)}
            disabled={!activeTerritory}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={activeTerritory ? 'Exporter les données du territoire' : 'Sélectionnez un territoire'}
            className="flex items-center gap-1.5 rounded-[11px] bg-navy px-3 py-[7px] font-sans text-xs font-medium text-white transition-colors hover:bg-navy-600 disabled:opacity-50"
          >
            <Download size={13} />
            <span>Exporter</span>
            <ChevronDown size={12} />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-30 mt-1.5 w-56 overflow-hidden rounded-[11px] border border-line bg-white py-1 shadow-card"
            >
              {EXPORT_ITEMS.map(({ kind, label, icon: Icon }) => (
                <button
                  key={kind}
                  type="button"
                  role="menuitem"
                  onClick={() => download(kind)}
                  disabled={busy !== null}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-sans text-[12.5px] text-body transition-colors hover:bg-surface-subtle disabled:opacity-60"
                >
                  {busy === kind ? (
                    <Loader2 size={14} className="animate-spin text-muted" />
                  ) : (
                    <Icon size={14} className="text-muted" />
                  )}
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bell with red badge */}
        <button
          type="button"
          className="relative rounded-full p-2 transition-colors hover:bg-surface-subtle"
          aria-label="Notifications"
        >
          <Bell size={16} className="text-body" />
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-marianne" />
        </button>

        {/* Avatar */}
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-navy font-sans text-[11px] font-bold text-white"
          aria-label="Profil utilisateur"
        >
          EA
        </button>
      </div>
    </header>
  );
}
